import React, { Suspense, lazy, useState } from 'react';
import { getTheme, useThemeMode } from '../theme';
import type { PickedArea } from './AreaPicker';

export type { PickedArea };

const LazyAreaPicker = lazy(() => import('./AreaPicker'));

const AREA_RADII = [10, 25, 50, 100, 200];

export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Ligger platsen inom området? Saknar den koordinater räknas den som utanför. */
export function inArea(area: PickedArea, lat?: number | null, lng?: number | null): boolean {
  if (lat == null || lng == null) return false;
  return haversineKm(area, { lat, lng }) <= area.radiusKm;
}

export function MapPinIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></svg>;
}

/** Ett kartfel (Leaflet, nätverk) får aldrig ta ner resten av sidan. */
class MapErrorBoundary extends React.Component<{ height: number; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.error('[karta]', err); }
  render() {
    if (this.state.failed) return <div style={{ height: this.props.height, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#888' }}>Kartan kunde inte laddas</div>;
    return this.props.children;
  }
}

/** Välj område på karta — som "Välj område" på Qvestium. */
export function AreaPickerModal({ initial, points, onApply, onClear, onClose }: {
  initial: PickedArea | null;
  points: { lat: number; lng: number }[];
  onApply: (a: PickedArea) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(initial ? { lat: initial.lat, lng: initial.lng } : null);
  const [radiusKm, setRadiusKm] = useState(initial?.radiusKm ?? 50);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const count = point ? points.filter(p => inArea({ ...point, radiusKm }, p.lat, p.lng)).length : null;

  function useMyPosition() {
    if (!navigator.geolocation) { setError('Din webbläsare kan inte ge din position'); return; }
    setLocating(true); setError('');
    navigator.geolocation.getCurrentPosition(
      pos => { setPoint({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); },
      () => { setError('Kunde inte hämta din position'); setLocating(false); },
      { timeout: 10000, maximumAge: 600000 },
    );
  }

  const btn = (active: boolean): React.CSSProperties => ({ background: active ? T.amber : 'transparent', color: active ? '#fff' : T.text, border: `1px solid ${active ? T.amber : T.border}`, borderRadius: 4, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: T.surface, width: '100%', maxWidth: 640, margin: 16, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>Välj område</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Klicka på kartan där du vill jaga</div>
          </div>
          <button onClick={onClose} aria-label="Stäng" style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}>✕</button>
        </div>
        <MapErrorBoundary height={360}>
          <Suspense fallback={<div style={{ height: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.textMuted }}>Laddar karta…</div>}>
            <LazyAreaPicker value={point} radiusKm={radiusKm} onPick={setPoint} points={points} height={360} />
          </Suspense>
        </MapErrorBoundary>
        <div style={{ padding: '14px 20px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.textSub, marginRight: 4 }}>Inom</span>
            {AREA_RADII.map(r => <button key={r} onClick={() => setRadiusKm(r)} style={btn(r === radiusKm)}>{r} km</button>)}
            <button onClick={useMyPosition} disabled={locating} style={{ ...btn(false), marginLeft: 'auto' }}>{locating ? 'Hämtar…' : 'Min position'}</button>
          </div>
          {error && <div style={{ fontSize: 12, color: T.danger }}>{error}</div>}
          <div style={{ fontSize: 12, color: T.textMuted }}>
            {count == null ? 'Punkterna visar annonser och jakter med känd plats' : `${count} ${count === 1 ? 'annons eller jakt' : 'annonser och jakter'} i området`}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {initial && <button style={btn(false)} onClick={onClear}>Visa hela Sverige</button>}
            <button style={{ ...btn(true), opacity: point ? 1 : 0.5 }} disabled={!point} onClick={() => point && onApply({ ...point, radiusKm })}>Visa jakt här</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AreaFilterChip({ area, onEdit, onClear }: { area: PickedArea; onEdit: () => void; onClear: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.amberDim, color: T.text, fontSize: 12, padding: '6px 6px 6px 12px', borderRadius: 16, marginBottom: 16 }}>
      <MapPinIcon size={13} />
      <button onClick={onEdit} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: T.text, fontFamily: 'inherit' }}>Inom {area.radiusKm} km</button>
      <button onClick={onClear} aria-label="Rensa områdesfilter" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: T.textSub, padding: '0 4px' }}>×</button>
    </div>
  );
}
